import React from 'react';
import { TriangleAlert, X } from 'lucide-react';
import type { KatalogAuswahl, KatalogSprache } from '../../services/mangelKatalog/typen';
import { KatalogPikto } from './KatalogPikto';
import { katalogTexte } from './texte';

/**
 * Chip im Schritt „Details“ („Standardmangel per Torte“, Paket D):
 * „Standard: Absturz › Seitenschutz“ mit Piktogramm, [Ändern] und [×]
 * (je ≥ 44 px), darunter bei einer Lücke in der Vorlage der gelbe Hinweis
 * „Angabe ergänzen“. Keine Nummer, keine Umschalter (Entscheid 28.09.).
 */
export interface KatalogChipProps {
    /** Namen, z. B. ['Absturz', 'Seitenschutz']. */
    pfad: string[];
    pikto: string;
    /** Vorlage enthält eine Lücke („……“, „konkretisieren“) → gelber Hinweis. */
    platzhalter?: boolean;
    onAendern: () => void;
    onEntfernen: () => void;
    /** 'freitext' → „Thema:“ statt „Standard:“. */
    art?: KatalogAuswahl['art'];
    /** Feste Beschriftungen in der Katalogsprache; Standard deutsch (28.09.). */
    sprache?: KatalogSprache;
    /** Mehrfachauswahl (Paket L): Zahl der zusammengeführten Punkte → „· 2 Punkte“. */
    anzahl?: number;
}

export const KatalogChip: React.FC<KatalogChipProps> = ({
    pfad,
    pikto,
    platzhalter = false,
    onAendern,
    onEntfernen,
    art,
    sprache = 'de',
    anzahl,
}) => {
    const t = katalogTexte(sprache);
    const pfadText = pfad.filter(Boolean).join(' › ');
    const punkte = typeof anzahl === 'number' && anzahl > 1 ? t.punkte(anzahl) : '';
    const beschriftung = [pfadText, punkte].filter(Boolean).join(' · ');
    const praefix = art === 'freitext' ? t.themaPraefix : t.standardPraefix;

    return (
        <div className="overflow-hidden rounded-xl border border-stone-300 bg-white">
            <div className="flex items-center gap-2 pl-3">
                <KatalogPikto schluessel={pikto} groesse={28} />
                <p className="min-w-0 flex-1 py-2 text-[14px] leading-snug text-stone-800">
                    <span className="font-semibold text-stone-500">{praefix} </span>
                    <span className="font-semibold">{beschriftung || t.ohneThema}</span>
                </p>
                <button
                    type="button"
                    onClick={onAendern}
                    className="min-h-11 shrink-0 rounded-lg px-3 text-[13px] font-semibold text-stone-700 hover:bg-stone-100 active:bg-stone-200"
                >
                    {t.aendern}
                </button>
                <button
                    type="button"
                    onClick={onEntfernen}
                    aria-label={t.standardmangelEntfernen}
                    title={t.standardmangelEntfernen}
                    className="flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-lg text-stone-500 hover:bg-stone-100 active:bg-stone-200"
                >
                    <X size={18} aria-hidden="true" />
                </button>
            </div>

            {platzhalter && (
                <p role="note" className="flex items-start gap-2 border-t border-amber-200 bg-amber-50 px-3 py-2 text-[12px] font-semibold text-amber-800">
                    <TriangleAlert size={16} aria-hidden="true" className="mt-px shrink-0" />
                    <span>{t.angabeErgaenzen}</span>
                </p>
            )}
        </div>
    );
};
